import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import { AccountFormProps } from './types';
import { Modal, Spinner, SchemaForm } from '@v2/components';
import { useRoles, useCreateAccount, useUpdateAccount } from '@v2/hooks';
import { accountSchema, accountFields } from './schema';
import type { AccountFormValues } from './schema';

const AccountForm: React.FC<AccountFormProps> = ({
  account,
  onCancel,
  onCompleted,
}) => {
  const isEditing = !!account;
  const title = isEditing ? 'Edit Account' : 'Create Account';

  const { roles, loading: rolesLoading } = useRoles();
  const { createAccount, loading: createLoading, error: createError } = useCreateAccount();
  const { updateAccount, loading: updateLoading, error: updateError } = useUpdateAccount();

  const mutationLoading = createLoading || updateLoading;
  const mutationError = createError || updateError;

  const fields = useMemo(
    () =>
      accountFields.map((f) =>
        f.name === 'roleId'
          ? {
              ...f,
              options: roles.map((r) => ({
                value: String(r.id),
                label: r.name.replace(/([a-z])([A-Z])/g, '$1 $2'),
              })),
            }
          : f
      ),
    [roles]
  );

  const defaultValues = useMemo<Partial<AccountFormValues> | undefined>(() => {
    if (!account || roles.length === 0) return undefined;
    return {
      firstName: account.firstName || '',
      lastName: account.lastName || '',
      email: account.email || '',
      roleId: String(account.roleId),
      idpObjectId: account.idpObjectId || '',
    };
  }, [account, roles]);

  const handleSubmit = async (data: AccountFormValues) => {
    const payload = { ...data, roleId: Number(data.roleId) };
    if (isEditing && account) {
      const success = await updateAccount(account.id, { ...account, ...payload });
      if (success) {
        toast.success('Account updated successfully');
        onCompleted();
      }
    } else {
      const newAccountId = await createAccount(payload);
      if (newAccountId) {
        toast.success('Account created successfully');
        onCompleted(newAccountId);
      }
    }
  };

  return (
    <Modal isOpen={true} onClose={onCancel} title={title} size="lg">
      {rolesLoading ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
          <Spinner size="lg" className="tw-text-primary-600 tw-mb-3" />
          <span className="tw-text-neutral-500">Loading...</span>
        </div>
      ) : (
        <SchemaForm
          fields={fields}
          schema={accountSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          loading={mutationLoading}
          error={mutationError}
          columns={2}
          submitLabel={isEditing ? 'Save Changes' : 'Create Account'}
          onCancel={onCancel}
        />
      )}
    </Modal>
  );
};

export default AccountForm;
